import React, { useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Download, Upload, CheckCircle2, AlertCircle, FileSpreadsheet, Info,
  Rocket, Circle, Loader2, Lock,
} from "lucide-react";

/**
 * Bulk Data Import — download template → upload CSV → validate → preview → commit.
 * Tabs are shown in the correct dependency order so a fresh customer can onboard
 * end-to-end in one sitting.
 */
const ENTITIES = [
  {
    key: "branches",
    label: "1. Branches",
    file: "branches_template.csv",
    roles: ["crm_manager"],
    preview: ["code", "name", "city", "region", "status"],
    desc: "Create the branch master first — every other upload references branch codes (e.g., MUM, DEL).",
  },
  {
    key: "users",
    label: "2. Users",
    file: "users_template.csv",
    roles: ["crm_manager"],
    preview: ["email", "name", "role", "branch_ids", "reports_to"],
    desc: "Upload seniors first (COO → Regional → Branch → Process Manager) so 'reports_to_email' can resolve. Branches must exist.",
  },
  {
    key: "clients",
    label: "3. Clients",
    file: "clients_template.csv",
    roles: ["crm_manager"],
    preview: ["name", "industry", "status", "relationship_owner_id"],
    desc: "Relationship-owner email must already exist in the Users collection.",
  },
  {
    key: "campaigns",
    label: "4. Campaigns",
    file: "campaigns_template.csv",
    roles: ["crm_manager"],
    preview: ["client_name", "name", "type", "branch_ids", "process_manager_id"],
    desc: "Clients, branches, and the assigned Process Manager user must all exist first.",
  },
  {
    key: "kpis",
    label: "5. KPIs & Benchmarks",
    file: "kpis_template.csv",
    roles: ["crm_manager"],
    preview: ["kpi_name", "unit", "direction", "benchmark_value", "effective_from"],
    desc: "Seeds KPI definition + first benchmark per campaign. Direction must be higher_is_better or lower_is_better.",
  },
  {
    key: "opening-balances",
    label: "6. Opening Balances",
    file: "opening_balances_template.csv",
    roles: ["crm_manager", "accounts"],
    preview: ["client_name", "fy", "opening_amount", "notes"],
    desc: "One opening balance per client per financial year (e.g., FY2025-26). Safe to re-run — existing entries are overwritten.",
  },
  {
    key: "billing",
    label: "7. Billing History",
    file: "billing_template.csv",
    roles: ["crm_manager", "accounts"],
    preview: ["client_name", "month", "invoice_number", "invoice_amount"],
    desc: "Month-wise invoices + receipts (historical). Uses the same /api/billing/bulk-upload flow — upload Opening Balances first, then Billing History.",
    billingPath: true,
  },
];

export default function BulkImport() {
  const { user } = useAuth();
  const [tab, setTab] = useState("branches");
  const [mode, setMode] = useState("wizard"); // "wizard" | "per-entity"

  const visible = ENTITIES.filter((e) => e.roles.includes(user?.role));
  if (!visible.length) {
    return (
      <div className="p-8 text-slate-600" data-testid="bulk-import-no-access">
        You do not have permission to access bulk import.
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="bulk-import-page">
      <div>
        <div className="mptg-overline">Data Migration</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
          Bulk Data Import
        </h1>
        <p className="text-slate-500 mt-1 max-w-3xl">
          Upload your legacy data as CSV (or Excel) files to go live quickly.
          Use the <strong>Go-Live Wizard</strong> to process every entity in a single flow,
          or switch to <strong>Per-entity</strong> for surgical fixes later.
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="bulk-mode-toggle">
        <button
          onClick={() => setMode("wizard")}
          data-testid="mode-wizard"
          className={`px-4 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-2 transition ${
            mode === "wizard" ? "bg-[#2E86C1] text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Rocket className="w-4 h-4" /> Go-Live Wizard
        </button>
        <button
          onClick={() => setMode("per-entity")}
          data-testid="mode-per-entity"
          className={`px-4 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-2 transition ${
            mode === "per-entity" ? "bg-[#2E86C1] text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" /> Per-entity
        </button>
      </div>

      {mode === "wizard" ? (
        <GoLiveWizard entities={visible} />
      ) : (
        <>
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="bulk-import-howto">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">How this works</div>
              <ol className="list-decimal list-inside mt-1 space-y-0.5">
                <li>Download the template for the entity below.</li>
                <li>Fill it with your data (keep column names unchanged).</li>
                <li>Upload &rarr; review validation errors &rarr; fix &amp; re-upload or commit.</li>
                <li>Only rows with zero errors are committed.</li>
              </ol>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex flex-wrap h-auto p-1 gap-1 bg-slate-100" data-testid="bulk-import-tabs">
              {visible.map((e) => (
                <TabsTrigger
                  key={e.key}
                  value={e.key}
                  data-testid={`bulk-tab-${e.key}`}
                  className="text-xs md:text-sm data-[state=active]:bg-white data-[state=active]:text-[#2E86C1]"
                >
                  {e.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {visible.map((e) => (
              <TabsContent key={e.key} value={e.key} className="pt-5">
                <EntityPanel entity={e} />
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
}

function EntityPanel({ entity }) {
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const basePath = entity.billingPath
    ? "/billing/bulk-upload"
    : `/import/${entity.key}`;
  const templatePath = entity.billingPath
    ? "/billing/template"
    : `/import/${entity.key}/template`;

  const downloadTemplate = async () => {
    try {
      const t = localStorage.getItem("mptg_token");
      const res = await fetch(`${API}${templatePath}`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = entity.file;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Template download failed");
    }
  };

  const onValidate = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Select a CSV or Excel file first.");
      return;
    }
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${basePath}/validate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if ((data.valid_count || 0) === 0) {
        toast.warning("No valid rows found. Fix errors and re-upload.");
      } else {
        toast.success(`${data.valid_count} of ${data.total_count} rows valid.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!result) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/commit`, { rows: result.rows });
      toast.success(`Imported ${data.inserted} rows successfully.`);
      setResult(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`bulk-panel-${entity.key}`}>
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-6 h-6 text-[#2E86C1] mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-semibold text-slate-800">{entity.label}</div>
            <p className="text-sm text-slate-500 mt-1">{entity.desc}</p>
          </div>
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="border-slate-200"
            data-testid={`download-template-${entity.key}`}
          >
            <Download className="w-4 h-4 mr-1" /> Template
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
          <Input
            type="file"
            ref={fileRef}
            accept=".csv,.xlsx,.xls"
            data-testid={`bulk-file-${entity.key}`}
            className="max-w-sm"
          />
          <Button
            onClick={onValidate}
            disabled={busy}
            className="bg-[#2E86C1] hover:bg-[#2471A3] text-white"
            data-testid={`validate-${entity.key}`}
          >
            <Upload className="w-4 h-4 mr-1" />
            {busy ? "Validating…" : "Upload & Validate"}
          </Button>
          {result && result.valid_count > 0 && (
            <Button
              onClick={onCommit}
              disabled={busy}
              className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white"
              data-testid={`commit-${entity.key}`}
            >
              Commit {result.valid_count} valid row{result.valid_count === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      </div>

      {result && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`bulk-result-${entity.key}`}>
          <div className="flex flex-wrap items-center gap-3 text-sm pb-3 border-b border-slate-100">
            <span className="badge-green rounded px-2 py-0.5 font-semibold inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {result.valid_count} valid
            </span>
            {result.errors?.length > 0 && (
              <span className="badge-red rounded px-2 py-0.5 font-semibold inline-flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {result.errors.length} errors
              </span>
            )}
            <span className="text-slate-500 text-xs">Total rows: {result.total_count}</span>
          </div>
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full mptg-table text-sm">
              <thead>
                <tr>
                  <th>Row</th>
                  {entity.preview.map((k) => (<th key={k}>{k}</th>))}
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className={r._errors?.length ? "bg-red-50" : ""}>
                    <td className="tabular-nums">{r._row}</td>
                    {entity.preview.map((k) => (
                      <td key={k} className="truncate max-w-[180px]">
                        {renderCell(r[k])}
                      </td>
                    ))}
                    <td className="text-xs text-[#C0392B]">
                      {(r._errors || []).join("; ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function renderCell(v) {
  if (v == null || v === "") return <span className="text-slate-300">—</span>;
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? "" : "s"}` : <span className="text-slate-300">—</span>;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}


/* ---------------------- Go-Live Wizard ---------------------- */

function GoLiveWizard({ entities }) {
  // state per entity: { file, validating, committing, validated, committed, result }
  const [state, setState] = useState(() =>
    Object.fromEntries(entities.map((e) => [e.key, initStep()])),
  );
  const [running, setRunning] = useState(false);

  const setStep = (key, patch) =>
    setState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const pickedCount = entities.filter((e) => state[e.key]?.file).length;

  const runAll = async (phase /* "validate" | "commit" */) => {
    setRunning(true);
    try {
      for (const e of entities) {
        const st = state[e.key];
        if (!st.file) continue;
        if (phase === "commit" && (!st.validated || (st.result?.valid_count ?? 0) === 0)) continue;

        const basePath = e.billingPath ? "/billing/bulk-upload" : `/import/${e.key}`;
        try {
          if (phase === "validate") {
            setStep(e.key, { validating: true });
            const fd = new FormData();
            fd.append("file", st.file);
            const { data } = await api.post(`${basePath}/validate`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            setStep(e.key, {
              validating: false,
              validated: true,
              result: data,
              committed: false,
            });
          } else {
            setStep(e.key, { committing: true });
            const { data } = await api.post(`${basePath}/commit`, {
              rows: state[e.key].result.rows,
            });
            setStep(e.key, {
              committing: false,
              committed: true,
              inserted: data.inserted,
            });
          }
        } catch (err) {
          setStep(e.key, { validating: false, committing: false, error: formatApiError(err.response?.data?.detail) || err.message });
          toast.error(`${e.label} — ${phase} failed: ${err.response?.data?.detail || err.message}`);
          break; // stop the pipeline on first hard error
        }
      }
      if (phase === "validate") toast.success("Validation run complete.");
      else toast.success("Commit run complete.");
    } finally {
      setRunning(false);
    }
  };

  const canCommitAny = entities.some(
    (e) => state[e.key]?.validated && (state[e.key]?.result?.valid_count || 0) > 0 && !state[e.key]?.committed,
  );

  return (
    <div className="space-y-4" data-testid="go-live-wizard">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading font-semibold text-slate-800 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-[#2E86C1]" /> Go-Live Wizard
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Attach a file for every entity you have, then run the pipeline in one click.
              Validation and commit both happen <strong>in dependency order</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => runAll("validate")}
              disabled={running || pickedCount === 0}
              variant="outline"
              className="border-slate-200"
              data-testid="wizard-validate-all"
            >
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Validate all ({pickedCount})
            </Button>
            <Button
              onClick={() => runAll("commit")}
              disabled={running || !canCommitAny}
              className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white"
              data-testid="wizard-commit-all"
            >
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Commit all in order
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <table className="w-full mptg-table text-sm">
          <thead>
            <tr>
              <th className="w-10"></th>
              <th>Entity</th>
              <th>Template</th>
              <th>File</th>
              <th>Validation</th>
              <th>Commit</th>
            </tr>
          </thead>
          <tbody>
            {entities.map((e, idx) => {
              const st = state[e.key];
              return (
                <tr key={e.key} data-testid={`wizard-row-${e.key}`}>
                  <td className="text-center text-xs text-slate-400 font-mono">{idx + 1}</td>
                  <td>
                    <div className="font-medium text-slate-800">{e.label}</div>
                    <div className="text-xs text-slate-500 max-w-md">{e.desc}</div>
                  </td>
                  <td>
                    <WizardTemplateLink entity={e} />
                  </td>
                  <td>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      data-testid={`wizard-file-${e.key}`}
                      onChange={(ev) =>
                        setStep(e.key, {
                          file: ev.target.files?.[0] || null,
                          validated: false,
                          committed: false,
                          result: null,
                          error: null,
                        })
                      }
                      className="max-w-[220px]"
                    />
                  </td>
                  <td data-testid={`wizard-validation-${e.key}`}>
                    <StatusCell step={st} phase="validate" />
                  </td>
                  <td data-testid={`wizard-commit-${e.key}`}>
                    <StatusCell step={st} phase="commit" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500">
        Tip: if any step shows errors, switch to <strong>Per-entity</strong> mode, fix the specific file, and re-run.
      </div>
    </div>
  );
}

function initStep() {
  return {
    file: null, validating: false, committing: false,
    validated: false, committed: false, result: null, inserted: 0, error: null,
  };
}

function WizardTemplateLink({ entity }) {
  const path = entity.billingPath ? "/billing/template" : `/import/${entity.key}/template`;
  const download = async () => {
    const t = localStorage.getItem("mptg_token");
    const res = await fetch(`${API}${path}`, {
      credentials: "include",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) { toast.error("Template download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = entity.file; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <button
      onClick={download}
      className="text-[#2E86C1] text-xs inline-flex items-center gap-1 hover:underline"
      data-testid={`wizard-template-${entity.key}`}
    >
      <Download className="w-3.5 h-3.5" /> CSV
    </button>
  );
}

function StatusCell({ step, phase }) {
  if (!step.file) {
    return <span className="text-slate-300 text-xs inline-flex items-center gap-1"><Lock className="w-3 h-3" /> —</span>;
  }
  if (phase === "validate") {
    if (step.validating) return <Pill tone="blue" icon={Loader2} spin>Validating…</Pill>;
    if (step.error && !step.validated) return <Pill tone="red" icon={AlertCircle}>{step.error.slice(0, 60)}</Pill>;
    if (!step.validated) return <Pill tone="slate" icon={Circle}>Pending</Pill>;
    const valid = step.result?.valid_count || 0;
    const errors = step.result?.errors?.length || 0;
    if (errors > 0) {
      return <Pill tone={valid > 0 ? "amber" : "red"} icon={AlertCircle}>{valid} valid · {errors} errors</Pill>;
    }
    return <Pill tone="green" icon={CheckCircle2}>{valid} valid</Pill>;
  }
  // commit
  if (step.committing) return <Pill tone="blue" icon={Loader2} spin>Committing…</Pill>;
  if (step.committed) return <Pill tone="green" icon={CheckCircle2}>Imported {step.inserted}</Pill>;
  if (!step.validated || (step.result?.valid_count || 0) === 0)
    return <Pill tone="slate" icon={Circle}>—</Pill>;
  return <Pill tone="slate" icon={Circle}>Ready</Pill>;
}

function Pill({ tone, icon: Icon, spin, children }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      <Icon className={`w-3.5 h-3.5 ${spin ? "animate-spin" : ""}`} />
      {children}
    </span>
  );
}
